import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Alert, Button, Card, Form, Input, Typography } from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { useUserStore } from "@/views/stores";
import { Permission } from "@/views/components/Permission";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";

export interface EnterpriseCustomQuery {
    page: number;
    size: number;
    systemName?: string;
    /** 兼容旧 Vue 企业定制列表的用户账号搜索参数。 */
    userInfo?: string;
}

export interface EnterpriseCustomRow {
    id: number | string;
    systemName?: string;
    customJson?: { institutionName?: string; realName?: string; mobile?: string; code?: string };
}

export interface EnterpriseCustomResult {
    list?: EnterpriseCustomRow[];
    total?: number;
}

/** 复用 Vue 企业定制分页接口。 */
export async function loadEnterpriseCustomRecords(query: EnterpriseCustomQuery): Promise<EnterpriseCustomResult> {
    const { data } = await request.service.get<EnterpriseCustomResult>("/enterprise-custom/paging", { params: query });
    return data;
}

const customerText = (customer?: EnterpriseCustomRow["customJson"]) =>
    customer
        ? [customer.institutionName || customer.realName, customer.mobile, customer.code].filter(Boolean).join(" | ")
        : DEFAULT_VALUE;

/** 企业定制列表最小闭环，保留 Vue 查询字段、核心列、空态和失败反馈。 */
export function EnterpriseCustomPage({
    loadRecords = loadEnterpriseCustomRecords
}: {
    loadRecords?: (query: EnterpriseCustomQuery) => Promise<EnterpriseCustomResult>;
}) {
    const navigate = useNavigate();
    const banButton = useUserStore(state => state.banButton);
    const [rows, setRows] = useState<EnterpriseCustomRow[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<EnterpriseCustomQuery>({ page: 1, size: 10 });
    const [systemName, setSystemName] = useState("");
    // 保留旧 Vue 的“用户账号”筛选值，提交时映射为后端约定的 userInfo 字段。
    const [userInfo, setUserInfo] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        let disposed = false;
        void loadRecords(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setError("");
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setError("企业定制列表加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadRecords, query]);

    // 用 zhihao-ui-react 列契约承接旧 Vue 企业定制列表的字段和操作插槽。
    const columns: TableColumnProps<EnterpriseCustomRow>[] = [
        {
            key: "id",
            label: "序号",
            width: 92,
            prop: "id",
            // 旧 Vue 使用 ProTable 的 index 列，分页后序号仍应是列表行号而非业务主键。
            render: ({ rowIndex }) => (query.page - 1) * query.size + rowIndex + 1
        },
        {
            key: "systemName",
            label: "系统名称",
            minWidth: 92,
            prop: "systemName",
            render: ({ row }) => (
                <Button
                    autoInsertSpace={false}
                    aria-label={`查看${row.systemName || DEFAULT_VALUE}`}
                    type="link"
                    onClick={() => void navigate(`/lianYunUser/enterpriseCustom/info/${row.id}`)}
                >
                    {row.systemName || DEFAULT_VALUE}
                </Button>
            )
        },
        {
            key: "customJson",
            label: "用户账号",
            minWidth: 120,
            prop: "customJson",
            render: ({ row }) => customerText(row.customJson)
        },
        {
            key: "operation",
            label: "操作",
            fixed: "right",
            width: 240,
            prop: "id",
            render: ({ row }) => (
                <Permission anyOf={["ROLE_CUSTOMIZED_W"]}>
                    <Button
                        autoInsertSpace={false}
                        disabled={banButton}
                        type="link"
                        onClick={() => void navigate(`/lianYunUser/enterpriseCustom/form/${row.id}`)}
                    >
                        修改
                    </Button>
                </Permission>
            )
        }
    ];

    return (
        <section aria-label="enterprise-custom-page">
            <Card>
                <ZhTable<EnterpriseCustomRow>
                    data={rows}
                    columns={normalizeOperationColumns(columns)}
                    emptyText="暂无企业定制"
                    pagination={false}
                    rowKey="id"
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title level={4}>企业定制</Typography.Title>

                            {error ? <Alert role="alert" title={error} type="error" /> : null}

                            <Form className="admin-table-filter-form" layout="inline">
                                <Permission anyOf={["ROLE_CUSTOMIZED_W"]}>
                                    <Button
                                        autoInsertSpace={false}
                                        disabled={banButton}
                                        type="primary"
                                        onClick={() => void navigate("/lianYunUser/enterpriseCustom/add")}
                                    >
                                        新增
                                    </Button>
                                </Permission>
                                <Form.Item>
                                    <Input
                                        aria-label="系统名称"
                                        placeholder="请输入系统名称"
                                        value={systemName}
                                        onChange={event => setSystemName(event.target.value)}
                                    />
                                </Form.Item>
                                <Form.Item>
                                    <Input
                                        aria-label="用户账号"
                                        placeholder="请输入账号信息搜索"
                                        value={userInfo}
                                        onChange={event => setUserInfo(event.target.value)}
                                    />
                                </Form.Item>
                                <Button
                                    autoInsertSpace={false}
                                    icon={<TableSearchIcon name="search" />}
                                    type="primary"
                                    onClick={() =>
                                        setQuery({
                                            page: 1,
                                            size: 10,
                                            ...(systemName ? { systemName } : {}),
                                            ...(userInfo ? { userInfo } : {})
                                        })
                                    }
                                >
                                    查询
                                </Button>
                                <Button
                                    autoInsertSpace={false}
                                    icon={<TableSearchIcon name="reset" />}
                                    onClick={() => {
                                        setSystemName("");
                                        setUserInfo("");
                                        setQuery({ page: 1, size: 10 });
                                    }}
                                >
                                    重置
                                </Button>
                            </Form>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                hideOnSinglePage
                                showSizeChanger
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                onChange={(page, size) =>
                                    setQuery(current => ({ ...current, page: size === current.size ? page : 1, size }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

export default EnterpriseCustomPage;
